"use client";

import { useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import ReactPaginate from "react-paginate";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { PAGE_SIZE_OPTIONS } from "@/config";
import { Select } from "@/components/ui";

/**
 * react-paginate hardcodes `role="navigation"` and `aria-label="Pagination"`
 * on its inner <ul>. ARIA doesn't allow role=navigation on <ul> (lighthouse
 * a11y violation), and we already wrap with <nav aria-label="Pagination">,
 * so the inner attrs are duplicates. Strip them after mount.
 */
function useStripPaginateRole(rootRef: React.RefObject<HTMLElement | null>) {
  useEffect(() => {
    const node = rootRef.current;
    if (!node) return;
    node.querySelectorAll("ul[role='navigation']").forEach((ul) => {
      ul.removeAttribute("role");
      ul.removeAttribute("aria-label");
    });
  });
}

/**
 * Real `href`s on the page links. react-paginate renders bare <a> tags
 * without one, so a crawler saw 15 games per list and no way to page 2 — the
 * rest of every list was unreachable. With an href it still intercepts the
 * click (preventDefault → onPageChange), so navigation is unchanged; the href
 * serves crawlers, middle-click and "open in new tab".
 *
 * The server renders `path?page=N` (all it knows). After mount the href keeps
 * the rest of the query — sort, region, page size, filters — so a new tab
 * opens the same view. `deps` re-read the URL after the list's own
 * history.pushState updates it.
 */
function usePageHrefBuilder(deps: unknown[]) {
  const pathname = usePathname();
  const [search, setSearch] = useState<string | null>(null);

  useEffect(() => {
    setSearch(window.location.search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return (page: number) => {
    const params = new URLSearchParams(search ?? "");
    if (page > 1) params.set("page", String(page));
    else params.delete("page");
    const qs = params.toString();
    return qs ? `${pathname}?${qs}` : pathname;
  };
}

type PaginationControlsProps = {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  pageSize: number;
  onPageSizeChange: (pageSize: number) => void;
  disabled?: boolean;
  pageRangeDisplayed?: number;
  marginPagesDisplayed?: number;
};

function formatPageSizeLabel(value: number): string {
  if (value >= 1000) {
    return `${Math.floor(value / 1000)}K`;
  }
  return String(value);
}

export function PaginationControls({
  currentPage,
  totalPages,
  onPageChange,
  pageSize,
  onPageSizeChange,
  disabled = false,
  pageRangeDisplayed = 5,
  marginPagesDisplayed = 1,
}: PaginationControlsProps) {
  const [pageInput, setPageInput] = useState<string>("");
  const [isMobile, setIsMobile] = useState(false);
  const hrefBuilder = usePageHrefBuilder([currentPage, pageSize, totalPages]);

  useEffect(() => {
    const updateBreakpoint = () => {
      setIsMobile(window.innerWidth < 640);
    };

    updateBreakpoint();
    window.addEventListener("resize", updateBreakpoint);
    return () => window.removeEventListener("resize", updateBreakpoint);
  }, []);

  if (totalPages <= 1) {
    return null;
  }

  const handlePageClick = (event: { selected: number }) => {
    if (disabled) return;
    const newPage = event.selected + 1;
    if (newPage !== currentPage) {
      onPageChange(newPage);
    }
  };

  const handlePageInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === "" || /^\d+$/.test(value)) {
      setPageInput(value);
    }
  };

  const handlePageInputSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (disabled || !pageInput) return;

    const pageNum = parseInt(pageInput, 10);
    if (
      !isNaN(pageNum) &&
      pageNum >= 1 &&
      pageNum <= totalPages &&
      pageNum !== currentPage
    ) {
      onPageChange(pageNum);
      setPageInput("");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handlePageInputSubmit(e as unknown as React.FormEvent);
    }
  };

  const disabledClass = disabled
    ? "opacity-40 cursor-not-allowed pointer-events-none"
    : "";

  const isFirstPage = currentPage === 1;
  const isLastPage = currentPage === totalPages;

  const displayPageRange = isMobile
    ? Math.min(3, pageRangeDisplayed)
    : pageRangeDisplayed;
  const displayMarginPages = isMobile
    ? Math.min(1, marginPagesDisplayed)
    : marginPagesDisplayed;

  const btn =
    "h-8 min-w-[32px] px-2 flex items-center justify-center border border-[#2a475e] rounded-md text-xs font-medium transition-colors text-[#c7d5e0] hover:bg-[#1f2e3f] hover:border-[#4a7a9b] hover:text-white";

  return (
    <NavWithRoleStrip className={`pt-4 ${disabledClass}`}>
      {/* Desktop: 3-column grid so pages stay perfectly centered */}
      <div className="hidden sm:grid sm:grid-cols-[1fr_auto_1fr] sm:items-center sm:gap-3">
        {/* Left: Show */}
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-[#8f98a0]">Show</span>
          <Select
            id="page-size-select"
            value={String(pageSize)}
            onChange={(value) => {
              if (disabled) return;
              const next = Number.parseInt(value, 10);
              if (!Number.isFinite(next)) return;
              onPageSizeChange(next);
            }}
            disabled={disabled}
            ariaLabel="Entries per page"
            className="!h-8 !py-0 !text-xs font-medium"
            options={(PAGE_SIZE_OPTIONS as unknown as number[]).map((opt) => ({
              value: String(opt),
              label: formatPageSizeLabel(opt),
            }))}
          />
        </div>

        {/* Center: page buttons */}
        <ReactPaginate
          breakLabel="…"
          nextLabel={<ChevronRight className="w-3.5 h-3.5" />}
          onPageChange={handlePageClick}
          pageRangeDisplayed={displayPageRange}
          marginPagesDisplayed={displayMarginPages}
          pageCount={totalPages}
          previousLabel={<ChevronLeft className="w-3.5 h-3.5" />}
          forcePage={currentPage - 1}
          hrefBuilder={hrefBuilder}
          containerClassName="flex items-center gap-1"
          pageLinkClassName={btn}
          previousClassName={isFirstPage ? "hidden" : ""}
          previousLinkClassName={btn}
          nextClassName={isLastPage ? "hidden" : ""}
          nextLinkClassName={btn}
          breakLinkClassName="h-8 px-1 flex items-center text-[#8f98a0] text-xs select-none"
          activeLinkClassName="!bg-[#1f2e3f] !border-[#66c0f4] !text-[#66c0f4]"
        />

        {/* Right: Go to */}
        <form
          onSubmit={handlePageInputSubmit}
          className="flex items-center gap-1.5 justify-end"
        >
          <span className="text-xs text-[#8f98a0]">Go to</span>
          <input
            id="page-input"
            type="text"
            value={pageInput}
            onChange={handlePageInputChange}
            onKeyDown={handleKeyDown}
            placeholder={String(currentPage)}
            disabled={disabled}
            className="w-12 h-8 px-2 border border-[#2a475e] rounded-md bg-[#16202d] text-white text-xs font-medium text-center focus:outline-none focus:border-[#66c0f4] focus:ring-1 focus:ring-[#66c0f4]/30 disabled:opacity-40 disabled:cursor-not-allowed"
          />
          <button
            type="submit"
            disabled={disabled || !pageInput}
            className="h-8 px-3 border border-[#2a475e] rounded-md text-[#c7d5e0] text-xs font-medium transition-colors hover:bg-[#1f2e3f] hover:border-[#4a7a9b] hover:text-white disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Go
          </button>
        </form>
      </div>

      {/* Mobile: stacked */}
      <div className="flex flex-col items-center gap-2 sm:hidden">
        <ReactPaginate
          breakLabel="…"
          nextLabel={<ChevronRight className="w-3.5 h-3.5" />}
          onPageChange={handlePageClick}
          pageRangeDisplayed={displayPageRange}
          marginPagesDisplayed={displayMarginPages}
          pageCount={totalPages}
          previousLabel={<ChevronLeft className="w-3.5 h-3.5" />}
          forcePage={currentPage - 1}
          hrefBuilder={hrefBuilder}
          containerClassName="flex items-center gap-1 flex-wrap justify-center"
          pageLinkClassName={btn}
          previousClassName={isFirstPage ? "hidden" : ""}
          previousLinkClassName={btn}
          nextClassName={isLastPage ? "hidden" : ""}
          nextLinkClassName={btn}
          breakLinkClassName="h-8 px-1 flex items-center text-[#8f98a0] text-xs select-none"
          activeLinkClassName="!bg-[#1f2e3f] !border-[#66c0f4] !text-[#66c0f4]"
        />

        <div className="flex items-center gap-2">
          <Select
            id="page-size-select-mobile"
            value={String(pageSize)}
            onChange={(value) => {
              if (disabled) return;
              const next = Number.parseInt(value, 10);
              if (!Number.isFinite(next)) return;
              onPageSizeChange(next);
            }}
            disabled={disabled}
            ariaLabel="Entries per page"
            className="!h-8 !py-0 !text-xs font-medium"
            options={(PAGE_SIZE_OPTIONS as unknown as number[]).map((opt) => ({
              value: String(opt),
              label: formatPageSizeLabel(opt),
            }))}
          />

          <form
            onSubmit={handlePageInputSubmit}
            className="flex items-center gap-1.5"
          >
            <input
              type="text"
              value={pageInput}
              onChange={handlePageInputChange}
              onKeyDown={handleKeyDown}
              placeholder={String(currentPage)}
              disabled={disabled}
              className="w-12 h-8 px-2 border border-[#2a475e] rounded-md bg-[#16202d] text-white text-xs font-medium text-center focus:outline-none focus:border-[#66c0f4] focus:ring-1 focus:ring-[#66c0f4]/30 disabled:opacity-40 disabled:cursor-not-allowed"
            />
            <button
              type="submit"
              disabled={disabled || !pageInput}
              className="h-8 px-3 border border-[#2a475e] rounded-md text-[#c7d5e0] text-xs font-medium transition-colors hover:bg-[#1f2e3f] hover:border-[#4a7a9b] hover:text-white disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Go
            </button>
          </form>
        </div>
      </div>
    </NavWithRoleStrip>
  );
}

/**
 * <nav aria-label="Pagination"> + post-mount cleanup of the inner
 * react-paginate <ul role="navigation"> (see useStripPaginateRole above).
 */
function NavWithRoleStrip({
  className,
  children,
}: {
  className?: string;
  children: React.ReactNode;
}) {
  const ref = useRef<HTMLElement>(null);
  useStripPaginateRole(ref);
  return (
    <nav ref={ref} aria-label="Pagination" className={className}>
      {children}
    </nav>
  );
}
